import React, { useState } from "react";
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, CircularProgress, FormControl, InputLabel, MenuItem, Select, Button, IconButton } from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteForever from "@mui/icons-material/DeleteForever";
import { Strategy as StrategyType, useListStrategiesQuery, useListStrategyGroupsQuery, useUpdateStrategyGroupMutation } from "features/trading/accounts";
import { ConfirmModal } from "components/ui/ConfirmModal";
import CreateStrategyModal from "./CreateStrategyModal";

const Strategy = () => {
  const [selectedGroup, setSelectedGroup] = useState<number | "">("");
  const [modalOpen, setModalOpen] = useState(false);
  const [positionToRemove, setPositionToRemove] = useState<StrategyType | null>(null);
  const { data: groupsData, isLoading: isGroupsLoading } = useListStrategyGroupsQuery({ pagination: { page: 0, pageSize: 100 } });
  const { data, isLoading, isError } = useListStrategiesQuery({
    pagination: { page: 0, pageSize: 100 },
    sorting: [{ field: "name", sort: "asc" }],
    filters: selectedGroup ? { strategy_group: selectedGroup } : {},
  });
  const [updateStrategyGroup, { isLoading: isRemoving }] = useUpdateStrategyGroupMutation();

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const selectedGroupName =
    groupsData?.results?.find((group) => group.id === selectedGroup)?.name ?? "";

  const handleRemoveStrategy = async () => {
    if (!positionToRemove) return;
    try {
      await updateStrategyGroup({ id: positionToRemove.id, strategy_group: null }).unwrap();
    } catch (error) {
      console.error('Failed to remove strategy:', error);
    } finally {
      setPositionToRemove(null);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Strategy Management
      </Typography>
      <Box display="flex" alignItems="center" mb={2}>
        <FormControl sx={{ minWidth: 300, mr: 2 }}>
          <InputLabel>Strategy</InputLabel>
          <Select
            value={selectedGroup}
            label="Strategy"
            onChange={(e) => setSelectedGroup(e.target.value as number | "")}
            disabled={isGroupsLoading}
          >
            {groupsData?.results?.map((group) => (
              <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedGroup && (
          <Button
            variant="contained"
            onClick={handleOpenModal}
          >
            New Position
          </Button>
        )}
      </Box>
      {selectedGroup && (
        <Card sx={{ maxWidth: 500, mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Positions
            </Typography>
            {isLoading ? (
              <CircularProgress />
            ) : isError ? (
              <Typography color="error">Failed to load positions.</Typography>
            ) : (
              <List>
                {data?.results && data.results.length > 0 ? (
                  data.results.map((strategy) => (
                    <ListItem
                      key={strategy.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => setPositionToRemove(strategy)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={strategy.name} />
                    </ListItem>
                  ))
                ) : (
                  <Typography>No positions found.</Typography>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      )}
      <CreateStrategyModal
        open={modalOpen}
        onClose={handleCloseModal}
        strategyGroupId={selectedGroup as number}
      />
      <ConfirmModal
        open={positionToRemove !== null}
        title="Removing the Position"
        description={
          positionToRemove
            ? `Are you sure you want to remove ${positionToRemove.name} from strategy ${selectedGroupName}?`
            : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
        confirmIcon={<DeleteForever />}
        confirming={isRemoving}
        onClose={() => setPositionToRemove(null)}
        onConfirm={handleRemoveStrategy}
      />
    </Box>
  );
};

export default Strategy;
